import { Outlet, Link, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  PlusCircleIcon,
  ListBulletIcon,
  CheckCircleIcon,
  PowerIcon,
  BanknotesIcon,
  StarIcon,
  TruckIcon,
  CreditCardIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import { LayoutDashboard, PieChart, Sun, Moon, User } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

export default function SidebarLayout({ userEmail, userRole }) {
  const location = useLocation();
  const { instance } = useMsal();
  const { theme, toggleTheme } = useTheme();

  // Extract and clean display name (strip designation/titles)
  const accounts = instance?.getAllAccounts?.() || [];
  const activeAccount = instance?.getActiveAccount?.() || (accounts.length > 0 ? accounts[0] : null);
  
  const getCleanName = () => {
    let raw = activeAccount?.name || '';
    if (!raw && userEmail) {
      raw = userEmail.split('@')[0].replace(/[._-]/g, ' ');
    }
    if (!raw) return 'User';

    // 1. Remove text inside parentheses, brackets, or braces e.g. "(Software Developer)" or "[IT]"
    let clean = raw.replace(/\s*[\(\[\{].*?[\)\]\}]/g, '').trim();

    // 2. Remove designation after hyphen, pipe, slash, or colon e.g. "Priyal Makwana - IT"
    if (clean.includes(' - ')) {
      clean = clean.split(' - ')[0].trim();
    } else if (clean.includes(' | ')) {
      clean = clean.split(' | ')[0].trim();
    } else if (clean.includes(' / ')) {
      clean = clean.split(' / ')[0].trim();
    } else if (clean.includes(':')) {
      clean = clean.split(':')[0].trim();
    }

    // 3. Format if "LastName, FirstName"
    if (clean.includes(',') && !clean.toLowerCase().includes('llp') && !clean.toLowerCase().includes('ltd')) {
      const parts = clean.split(',').map(p => p.trim());
      if (parts.length === 2) {
        clean = `${parts[1]} ${parts[0]}`;
      }
    }

    return clean.trim() || 'User';
  };

  const displayName = getCleanName();

  const navItemsByRole = {
    admin: [
      { path: '/inventory-dashboard', label: 'Procurement Dashboard', icon: <LayoutDashboard className="h-5 w-5" /> },
      { path: '/budget-dashboard', label: 'Budget Dashboard', icon: <PieChart className="h-5 w-5" /> },
      { path: '/new-expense', label: 'New Expense', icon: <PlusCircleIcon className="h-5 w-5" /> },
      { path: '/expenses', label: 'Expense List', icon: <ListBulletIcon className="h-5 w-5" /> },
      { path: '/approve', label: 'Approvals', icon: <CheckCircleIcon className="h-5 w-5" /> },
      { path: '/sent-to-payment', label: 'Payment Adjustment', icon: <BanknotesIcon className="h-5 w-5" /> },
      { path: '/payment-management', label: 'Payment Management', icon: <CreditCardIcon className="h-5 w-5" /> },
      { path: '/goods-receipts', label: 'Goods Receipts', icon: <TruckIcon className="h-5 w-5" /> },
      { path: '/feedback', label: 'Feedback', icon: <StarIcon className="h-5 w-5" /> },
      { path: '/faq', label: 'FAQ', icon: <QuestionMarkCircleIcon className="h-5 w-5" /> },
    ],
    approver: [
      { path: '/inventory-dashboard', label: 'Procurement Dashboard', icon: <LayoutDashboard className="h-5 w-5" /> },
      { path: '/budget-dashboard', label: 'Budget Dashboard', icon: <PieChart className="h-5 w-5" /> },
      { path: '/expenses', label: 'Expense List', icon: <ListBulletIcon className="h-5 w-5" /> },
      { path: '/approve', label: 'Approvals', icon: <CheckCircleIcon className="h-5 w-5" /> },
      { path: '/sent-to-payment', label: 'Payment Adjustment', icon: <BanknotesIcon className="h-5 w-5" /> },
      { path: '/payment-management', label: 'Payment Management', icon: <CreditCardIcon className="h-5 w-5" /> },
      { path: '/goods-receipts', label: 'Goods Receipts', icon: <TruckIcon className="h-5 w-5" /> },
      { path: '/feedback', label: 'Feedback', icon: <StarIcon className="h-5 w-5" /> },
      { path: '/faq', label: 'FAQ', icon: <QuestionMarkCircleIcon className="h-5 w-5" /> }
    ],
    creator: [
      { path: '/inventory-dashboard', label: 'Procurement Dashboard', icon: <LayoutDashboard className="h-5 w-5" /> },
      { path: '/budget-dashboard', label: 'Budget Dashboard', icon: <PieChart className="h-5 w-5" /> },
      { path: '/new-expense', label: 'New Expense', icon: <PlusCircleIcon className="h-5 w-5" /> },
      { path: '/expenses', label: 'Expense List', icon: <ListBulletIcon className="h-5 w-5" /> },
      { path: '/approve', label: 'Approvals', icon: <CheckCircleIcon className="h-5 w-5" /> },
      { path: '/sent-to-payment', label: 'Payment Adjustment', icon: <BanknotesIcon className="h-5 w-5" /> },
      { path: '/payment-management', label: 'Payment Management', icon: <CreditCardIcon className="h-5 w-5" /> },
      { path: '/goods-receipts', label: 'Goods Receipts', icon: <TruckIcon className="h-5 w-5" /> },
      { path: '/feedback', label: 'Feedback', icon: <StarIcon className="h-5 w-5" /> },
      { path: '/faq', label: 'FAQ', icon: <QuestionMarkCircleIcon className="h-5 w-5" /> }
    ],
    payment_team: [
      { path: '/payment-management', label: 'Payment Management', icon: <CreditCardIcon className="h-5 w-5" /> },
      { path: '/faq', label: 'FAQ', icon: <QuestionMarkCircleIcon className="h-5 w-5" /> },
    ],
    payment: [
      { path: '/inventory-dashboard', label: 'Procurement Dashboard', icon: <LayoutDashboard className="h-5 w-5" /> },
      { path: '/budget-dashboard', label: 'Budget Dashboard', icon: <PieChart className="h-5 w-5" /> },
      { path: '/expenses', label: 'Expense List', icon: <ListBulletIcon className="h-5 w-5" /> },
      { path: '/approve', label: 'Approvals', icon: <CheckCircleIcon className="h-5 w-5" /> },
      { path: '/sent-to-payment', label: 'Payment Adjustment', icon: <BanknotesIcon className="h-5 w-5" /> },
      { path: '/payment-management', label: 'Payment Management', icon: <CreditCardIcon className="h-5 w-5" /> },
      { path: '/goods-receipts', label: 'Goods Receipts', icon: <TruckIcon className="h-5 w-5" /> },
      { path: '/feedback', label: 'Feedback', icon: <StarIcon className="h-5 w-5" /> },
      { path: '/faq', label: 'FAQ', icon: <QuestionMarkCircleIcon className="h-5 w-5" /> }
    ],
    // Default/basic users
    user: [
      { path: '/inventory-dashboard', label: 'Procurement Dashboard', icon: <LayoutDashboard className="h-5 w-5" /> },
      { path: '/budget-dashboard', label: 'Budget Dashboard', icon: <PieChart className="h-5 w-5" /> },
      { path: '/expenses', label: 'Expense List', icon: <ListBulletIcon className="h-5 w-5" /> },
      { path: '/feedback', label: 'Feedback', icon: <StarIcon className="h-5 w-5" /> },
      { path: '/faq', label: 'FAQ', icon: <QuestionMarkCircleIcon className="h-5 w-5" /> }
    ]
  };

  // Fallback to admin for specific admin users if role fetch fails
  let effectiveRole = userRole;

  if (!userRole || userRole === 'user') {
    const adminEmails = [
      'tanvi.laddha@aucglobal.com',
    ];

    if (userEmail && adminEmails.includes(userEmail.toLowerCase())) {
      effectiveRole = 'admin';
    }
  }

  const userRoles = typeof effectiveRole === 'string' ? effectiveRole.split(',').map(r => r.trim()) : [effectiveRole || 'admin'];

  let navItems = [];
  userRoles.forEach(role => {
    const items = navItemsByRole[role] || navItemsByRole['user'] || [];
    items.forEach(item => {
      if (!navItems.find(n => n.path === item.path)) {
        navItems.push(item);
      }
    });
  });
  if (navItems.length === 0) navItems = navItemsByRole['admin'];

  return (
    <div className="flex h-screen w-full overflow-hidden bg-app" style={{ background: 'var(--app-bg)' }}>
      {/* 1. Left: Full-Height Main Sidebar (100vh) */}
      <aside
        className="main-sidebar flex flex-col flex-shrink-0"
        style={{
          width: '260px',
          minWidth: '260px',
          background: 'linear-gradient(180deg, #63171b 0%, #521216 40%, #3a0c0f 75%, #220507 100%)',
          height: '100vh',
          zIndex: 40
        }}
      >
        {/* Top Brand Card matching HRMS exactly */}
        <div style={{ padding: '14px 14px 10px 14px' }}>
          <div
            className="flex items-center gap-3 overflow-hidden"
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.14)',
              borderRadius: '12px',
              padding: '8px 12px',
              height: '48px',
              boxSizing: 'border-box'
            }}
          >
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0,
                padding: '2px',
                boxShadow: '0 2px 5px rgba(0,0,0,0.25)'
              }}
            >
              <img
                src="/acorn-logo.png"
                alt="Acorn Logo"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                onError={(e) => { e.target.src = '/acorn.png'; }}
              />
            </div>
            <div className="min-w-0 flex-1">
              <h1 style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff', margin: 0, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                Expense Management
              </h1>
            </div>
          </div>
        </div>

        {/* Sidebar Navigation Items */}
        <nav className="flex-1 px-3 py-2 space-y-1.5 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
          {navItems.map(item => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl transition-all text-sm font-medium"
                style={{
                  background: isActive ? 'rgba(255, 255, 255, 0.16)' : 'transparent',
                  border: isActive ? '1px solid rgba(255, 255, 255, 0.18)' : '1px solid transparent',
                  color: isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.88)',
                  fontWeight: isActive ? 600 : 500,
                  textDecoration: 'none',
                  boxShadow: isActive ? '0 1px 4px rgba(0, 0, 0, 0.2)' : 'none',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                    e.currentTarget.style.color = '#ffffff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.88)';
                  }
                }}
              >
                <span style={{ color: isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.8)', display: 'flex', alignItems: 'center' }}>
                  {item.icon}
                </span>
                <span className="truncate">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Bottom Copyright Strip */}
        <div
          style={{
            background: 'transparent',
            padding: '12px 14px',
            textAlign: 'center',
            fontSize: '11px',
            color: 'rgba(255, 255, 255, 0.5)',
            letterSpacing: '0.3px',
            borderTop: '1px solid rgba(255, 255, 255, 0.06)'
          }}
        >
          © 2026 AUC LLP
        </div>
      </aside>

      {/* 2. Right: Top Header Bar + Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        {/* Top Header Bar matching HRMS exactly */}
        <header
          className="flex items-center justify-end px-4 z-30 flex-shrink-0"
          style={{
            height: '62px',
            background: '#63171b'
          }}
        >
          {/* Right: Theme Toggle + User Profile Pill + Red Logout Button */}
          <div className="flex items-center gap-3">
            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="rounded-lg transition border flex items-center justify-center"
              title={theme === 'dark' ? 'Switch to Light mode' : 'Switch to Dark mode'}
              style={{
                width: '36px',
                height: '36px',
                background: 'rgba(255, 255, 255, 0.08)',
                borderColor: 'rgba(255, 255, 255, 0.16)',
                color: '#ffffff',
                cursor: 'pointer'
              }}
            >
              {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
            </button>

            {/* User Profile Pill (matching HRMS) */}
            <div
              className="flex items-center gap-2 px-3.5 rounded-full border shadow-sm transition cursor-default"
              style={{
                height: '36px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderColor: 'rgba(255, 255, 255, 0.18)',
                color: '#ffffff',
                fontSize: '12.5px',
                fontWeight: 600,
                letterSpacing: '0.3px'
              }}
              title={userEmail}
            >
              <User className="w-4 h-4 text-white opacity-90" />
              <span className="truncate max-w-[240px] uppercase">{displayName}</span>
            </div>

            {/* Red Pill Logout Button (matching HRMS) */}
            <button
              onClick={() => instance.logoutRedirect()}
              className="flex items-center gap-1.5 px-4 rounded-full font-semibold text-sm transition shadow-md"
              style={{
                height: '36px',
                background: '#d32f2f',
                color: '#ffffff',
                border: 'none',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#b71c1c'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#d32f2f'; }}
              title="Logout"
            >
              <PowerIcon className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </header>

        {/* Main Page Content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden" style={{ color: 'var(--text)' }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
